/**
 * Pure message-ordering helpers for team-chat.
 *
 * The backend returns GET /channels/:id/messages in `created_at` DESC (newest
 * first), while every chat surface renders oldest→newest. Historically each
 * consumer reordered inline (ChannelView did, DockChatWindow didn't — so the
 * dock rendered upside down). This module is the single source of truth:
 * consumers flatten/read the raw cache and pass it through `sortMessagesAsc`.
 *
 * No React here — plain .ts with a .test.ts sibling (same pattern as
 * slash-commands.ts / scroll-stick.ts).
 */

/**
 * Sort messages ascending by `created_at` (oldest first).
 *
 * - Stable: equal keys keep their relative input order (Array.prototype.sort
 *   is spec-stable since ES2019; we additionally tie-break for determinism).
 * - Ties on `created_at` break by `id` (lexicographic) so two clients with the
 *   same rows always render the same order.
 * - Messages with a missing/empty `created_at` sort to the END (they're
 *   usually optimistic/degenerate rows — keep them by the composer).
 * - Tolerates `undefined` input (query cache not loaded yet) → `[]`.
 *
 * Returns a new array; never mutates the input (React Query cache rows must
 * stay immutable).
 */
export function sortMessagesAsc<T extends { created_at?: string; id?: string }>(
  messages: T[] | undefined,
): T[] {
  if (!messages || messages.length === 0) return [];
  return [...messages].sort((a, b) => {
    const aTime = preciseTime(a.created_at);
    const bTime = preciseTime(b.created_at);
    const aMissing = aTime === undefined;
    const bMissing = bTime === undefined;
    if (aMissing !== bMissing) return aMissing ? 1 : -1; // missing → end
    if (aTime !== undefined && bTime !== undefined && aTime !== bTime) return aTime < bTime ? -1 : 1;
    // Tie-break by id for cross-client determinism; missing ids compare equal
    // and fall back to stable input order.
    const aId = a.id ?? '';
    const bId = b.id ?? '';
    if (aId !== bId) return aId < bId ? -1 : 1;
    return 0;
  });
}

/** Preserve native RFC3339Nano precision and normalize timezone offsets. */
function preciseTime(iso: string | undefined): bigint | undefined {
  if (!iso) return;
  const milliseconds = Date.parse(iso);
  if (!Number.isFinite(milliseconds)) return;
  const fraction = /\.(\d{1,9})(?:Z|[+-]\d\d:\d\d)$/.exec(iso)?.[1] ?? '';
  return BigInt(Math.floor(milliseconds / 1000)) * 1_000_000_000n + BigInt(fraction.padEnd(9, '0'));
}

/** The native seek tuple is created_at, then UUID, both ascending. */
export function oldestMessageId<T extends {created_at?: string;id?: string}>(page: T[]): string | undefined {
  return sortMessagesAsc(page.filter(row => !!row.id && preciseTime(row.created_at) !== undefined))[0]?.id;
}

export { preciseTime as chatInstant };

/** Native reply pages may overlap during refresh. History IDs remain unique. */
export function flattenThreadReplies(data?:import('./queries').ChatThreadData){
 const ids=new Set<string>();return sortMessagesAsc(data?.pages.flatMap(page=>page.replies).filter(row=>{if(!row.id||ids.has(row.id))return false;ids.add(row.id);return true;}));
}
